import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';
import { usePlatformIntegrationAuth } from '../../../../hooks/usePlatformIntegrationAuth';
import {
  IntegrationLoadingSpinner,
  isIntegrationFetchFailure,
} from '../../../../components/platform/IntegrationFetchState';
import axios from 'axios';
import {
  Globe,
  Shield,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Mail,
  ArrowRight,
  Info,
  Plus,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  DnsStatusBadge,
  OverallVerificationBadge,
  DomainVerificationPanel,
  DOMAIN_ASSIGNMENT_TYPES,
  DEFAULT_DOMAIN_STATUS,
  getDomainFromEmail,
  deriveTrackingDomain,
  deriveReplyDomain,
  evaluateVerificationResponse,
} from '../../../../components/platform/email/EmailDomainComponents';
import PlatformEmailDomainSetupFlow from '../../../../components/platform/email/PlatformEmailDomainSetupFlow';
import { platformSaveButtonClassName } from '../../../../components/platform/PlatformSaveButtonStyles';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '').replace(/\/api$/, '');
const API = `${API_BASE}/api`;

const DEFAULT_SETTINGS = {
  configured: false,
  mailgun_domain: '',
  from_email: '',
  reply_to_email: '',
  region: 'US',
  tracking_domain: '',
  reply_domain: '',
  domain_status: DEFAULT_DOMAIN_STATUS,
};

function buildFormSnapshotFromSettings(data = {}) {
  const mailgunDomain = (data.mailgun_domain || '').trim();
  return {
    mailgun_domain: mailgunDomain,
    region: data.region || 'US',
    tracking_domain: (data.tracking_domain || deriveTrackingDomain(mailgunDomain) || '').trim(),
    reply_domain: (
      data.reply_domain ||
      deriveReplyDomain(data.reply_to_email, mailgunDomain) ||
      ''
    ).trim(),
  };
}

function getDomainFormMissingFields(formData) {
  const missing = [];
  if (!formData.mailgun_domain?.trim()) {
    missing.push('mailgun_domain');
  }
  return missing;
}

function isDomainFormDirty(formData, savedSnapshot) {
  if (!savedSnapshot) return true;
  return (
    formData.mailgun_domain.trim() !== savedSnapshot.mailgun_domain ||
    formData.region !== savedSnapshot.region ||
    (formData.tracking_domain || '').trim() !== savedSnapshot.tracking_domain ||
    (formData.reply_domain || '').trim() !== savedSnapshot.reply_domain
  );
}

const PlatformDomainsSettings = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { authLoading, canFetch, requestConfig } = usePlatformIntegrationAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [formData, setFormData] = useState({
    mailgun_domain: '',
    region: 'US',
    tracking_domain: '',
    reply_domain: '',
  });
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [configUnavailable, setConfigUnavailable] = useState(false);
  const [view, setView] = useState('overview');
  const fetchInFlightRef = useRef(false);

  const fetchSettings = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/platform/settings/integrations/mailgun`, requestConfig());
      setConfigUnavailable(false);
      setSettings(res.data);
      const snapshot = buildFormSnapshotFromSettings(res.data);
      setSavedSnapshot(snapshot);
      setFormData(snapshot);
    } catch (err) {
      if (isIntegrationFetchFailure(err)) {
        setConfigUnavailable(true);
        setSettings(DEFAULT_SETTINGS);
      }
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  }, [requestConfig]);

  useEffect(() => {
    if (authLoading) return;
    if (!canFetch) {
      setLoading(false);
      return;
    }
    fetchSettings();
  }, [authLoading, canFetch, fetchSettings]);

  const updateForm = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'mailgun_domain' && !prev.tracking_domain) {
        next.tracking_domain = deriveTrackingDomain(value);
      }
      return next;
    });
  };

  const missingFields = getDomainFormMissingFields(formData);
  const isFormValid = missingFields.length === 0;
  const isDirty = isDomainFormDirty(formData, savedSnapshot);
  const canSave = isFormValid && !saving && !configUnavailable;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[PlatformDomainsSettings] save button state', {
        isValid: isFormValid,
        isDirty,
        isSaving: saving,
        missingFields,
        canSave,
        formData,
        savedSnapshot,
      });
    }
  }, [isFormValid, isDirty, saving, missingFields, canSave, formData, savedSnapshot]);

  const handleSaveDomains = async () => {
    if (configUnavailable) return;

    if (!isFormValid) {
      toast.error('Primary sending domain is required to save domain settings.');
      return;
    }

    if (!isDirty) {
      toast.info('Domain settings are already saved.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        mailgun_domain: formData.mailgun_domain.trim(),
        region: formData.region,
        tracking_domain: formData.tracking_domain?.trim() || null,
        reply_domain: formData.reply_domain?.trim() || null,
        from_name: settings.from_name || null,
        from_email: settings.from_email || null,
        reply_to_email: settings.reply_to_email || null,
        is_active: settings.is_active || false,
        use_as_platform_default: settings.use_as_platform_default ?? true,
        use_as_agency_fallback: settings.use_as_agency_fallback ?? true,
        track_opens: settings.track_opens ?? true,
        track_clicks: settings.track_clicks ?? true,
        enable_unsubscribe_tracking: settings.enable_unsubscribe_tracking ?? false,
        enable_bounce_logging: settings.enable_bounce_logging ?? true,
        environment: settings.environment || 'production',
      };

      const res = await axios.post(`${API}/platform/settings/integrations/mailgun`, payload, requestConfig());
      toast.success('Domain settings saved.');
      if (res.data?.domain_warning) {
        toast.warning(res.data.domain_warning);
      }
      const nextSnapshot = buildFormSnapshotFromSettings({
        ...settings,
        mailgun_domain: payload.mailgun_domain,
        region: payload.region,
        tracking_domain: payload.tracking_domain,
        reply_domain: payload.reply_domain,
      });
      setSavedSnapshot(nextSnapshot);
      setFormData(nextSnapshot);
      await fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save domain settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckDomain = async () => {
    if (!formData.mailgun_domain?.trim()) {
      toast.error('Configure a sending domain before checking verification.');
      return;
    }
    setChecking(true);
    try {
      const res = await axios.post(
        `${API}/platform/settings/integrations/mailgun/check-domain`,
        {},
        requestConfig()
      );
      const evaluation = evaluateVerificationResponse(res.data);
      await fetchSettings();

      if (res.data?.domain_warning) {
        toast.warning(res.data.domain_warning);
      }

      if (evaluation.level === 'success') {
        toast.success(evaluation.toastMessage);
      } else if (evaluation.level === 'error') {
        toast.error(evaluation.toastMessage);
      } else if (evaluation.level === 'warning') {
        toast.warning(evaluation.toastMessage);
      } else {
        toast.info(evaluation.toastMessage);
      }

      return {
        verified: evaluation.allVerified,
        message: evaluation.message,
        verification_status: res.data?.verification_status,
        dns_records: res.data?.dns_records || [],
        evaluation,
      };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check domain verification');
      throw new Error(err.response?.data?.detail || 'Failed to check domain verification');
    } finally {
      setChecking(false);
    }
  };

  const handleSaveDomainFromSetup = async (domain) => {
    setSaving(true);
    try {
      const payload = {
        mailgun_domain: domain,
        region: formData.region,
        tracking_domain: deriveTrackingDomain(domain),
        reply_domain: formData.reply_domain?.trim() || null,
        from_name: settings.from_name || null,
        from_email: settings.from_email || null,
        reply_to_email: settings.reply_to_email || null,
        is_active: settings.is_active || false,
        use_as_platform_default: settings.use_as_platform_default ?? true,
        use_as_agency_fallback: settings.use_as_agency_fallback ?? true,
        track_opens: settings.track_opens ?? true,
        track_clicks: settings.track_clicks ?? true,
        enable_unsubscribe_tracking: settings.enable_unsubscribe_tracking ?? false,
        enable_bounce_logging: settings.enable_bounce_logging ?? true,
        environment: settings.environment || 'production',
      };
      const res = await axios.post(`${API}/platform/settings/integrations/mailgun`, payload, requestConfig());
      const nextSnapshot = buildFormSnapshotFromSettings({
        ...settings,
        mailgun_domain: domain,
        tracking_domain: deriveTrackingDomain(domain),
        reply_domain: payload.reply_domain,
      });
      setSavedSnapshot(nextSnapshot);
      setFormData(nextSnapshot);
      await fetchSettings();
      return res.data;
    } finally {
      setSaving(false);
    }
  };

  const domainsSaveLabel = saving
    ? 'Saving...'
    : isDirty
      ? 'Save Domain Settings'
      : 'Saved';

  if (authLoading || loading) {
    return <IntegrationLoadingSpinner />;
  }

  const domainStatus = settings.domain_status || DEFAULT_DOMAIN_STATUS;
  const primaryDomain = formData.mailgun_domain || settings.mailgun_domain || '—';
  const fromDomain = getDomainFromEmail(settings.from_email) || primaryDomain.replace(/^mg\./, '');
  const trackingDomain = formData.tracking_domain || deriveTrackingDomain(primaryDomain);
  const replyDomain = formData.reply_domain || deriveReplyDomain(settings.reply_to_email, primaryDomain);
  const assignmentDomain = primaryDomain !== '—' ? primaryDomain : 'Not configured';
  const providerDnsRecords =
    settings?.dns_records || settings?.settings?.dns_records || [];

  const pageText = isDark ? 'text-white' : 'text-stone-900';
  const mutedText = isDark ? 'text-zinc-400' : 'text-stone-500';

  if (view === 'add') {
    return (
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="platform-domains-settings">
        <PlatformEmailDomainSetupFlow
          initialDomain={formData.mailgun_domain}
          domainStatus={domainStatus}
          providerDnsRecords={providerDnsRecords}
          onBack={() => setView('overview')}
          onSaveDomain={handleSaveDomainFromSetup}
          onVerifyDomain={handleCheckDomain}
          saving={saving}
          verifying={checking}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="platform-domains-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 rounded-xl">
            <Globe className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${pageText}`}>Domains</h1>
            <p className={`text-sm ${mutedText}`}>
              Email sending domains, DNS verification, and domain assignment by email type
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setView('add')}
            variant="outline"
            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            data-testid="add-sending-domain-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Sending Domain
          </Button>
          <Button
            onClick={handleSaveDomains}
            disabled={!canSave || saving}
            className={platformSaveButtonClassName({
              active: canSave && (isDirty || saving),
              saved: canSave && !isDirty && !saving,
              inactive: !canSave,
            })}
            data-testid="domains-save-btn"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : !isDirty && canSave ? (
              <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" />
            ) : null}
            {domainsSaveLabel}
          </Button>
        </div>
      </div>

      {configUnavailable && (
        <div className="flex items-start gap-3 p-4 rounded-xl border bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-sm text-zinc-300">
            Domain settings API is unavailable. Connect Mailgun under Integrations first.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-amber-500" />
                <div>
                  <CardTitle className="text-white">Platform Sending Domain</CardTitle>
                  <CardDescription className="text-zinc-400">
                    Primary domain used for platform email delivery
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Primary Sending Domain</Label>
                  <Input
                    placeholder="mg.fundelite.com"
                    value={formData.mailgun_domain}
                    onChange={(e) => updateForm('mailgun_domain', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="domains-primary-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Region</Label>
                  <Select value={formData.region} onValueChange={(v) => updateForm('region', v)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="EU">European Union</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">From Domain</Label>
                  <Input
                    value={fromDomain || '—'}
                    readOnly
                    className="bg-zinc-800/50 border-zinc-700 text-zinc-400"
                  />
                  <p className="text-xs text-zinc-500">Derived from default sender email on Mailgun settings</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Tracking Domain</Label>
                  <Input
                    placeholder="email.fundelite.com"
                    value={formData.tracking_domain}
                    onChange={(e) => updateForm('tracking_domain', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-zinc-400">Reply-To Domain</Label>
                  <Input
                    placeholder="fundelite.com"
                    value={formData.reply_domain}
                    onChange={(e) => updateForm('reply_domain', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                <p className="text-xs text-zinc-400">
                  API keys are managed on the{' '}
                  <Link
                    to="/platform/settings/integrations/mailgun"
                    className="text-amber-400 hover:text-amber-300 underline"
                  >
                    Mailgun integration
                  </Link>{' '}
                  page. Default sender name and email are optional fallbacks configured there.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <ArrowRight className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-white">Domain Assignment by Email Type</CardTitle>
              </div>
              <CardDescription className="text-zinc-400">
                Future-ready mapping for platform and agency email channels
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {DOMAIN_ASSIGNMENT_TYPES.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-zinc-800/40 border border-zinc-800"
                  >
                    <span className="text-sm text-zinc-300">{item.label}</span>
                    <span className="text-sm text-amber-400/90 font-mono">{assignmentDomain}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-amber-500" />
                  <CardTitle className="text-white text-base">Verification Status</CardTitle>
                </div>
                <OverallVerificationBadge verified={domainStatus.verified} domainStatus={domainStatus} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Provider</p>
                  <p className="text-zinc-200 flex items-center gap-1.5 mt-0.5">
                    <Mail className="w-3.5 h-3.5 text-amber-400" />
                    Mailgun
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Last Checked</p>
                  <p className="text-zinc-200 mt-0.5">
                    {domainStatus.last_checked_at
                      ? new Date(domainStatus.last_checked_at).toLocaleString()
                      : 'Never'}
                  </p>
                </div>
              </div>

              <DomainVerificationPanel
                domainStatus={domainStatus}
                onCheckDomain={handleCheckDomain}
                checking={checking}
                showMailgunLink
              />
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base">Domain Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {[
                { label: 'Sending', value: primaryDomain },
                { label: 'From', value: fromDomain || '—' },
                { label: 'Tracking', value: trackingDomain || '—' },
                { label: 'Reply-To', value: replyDomain || '—' },
              ].map((row) => (
                <div key={row.label} className="flex justify-between gap-4">
                  <span className="text-zinc-500">{row.label}</span>
                  <span className="text-zinc-300 font-mono text-right truncate">{row.value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 border-t border-zinc-800">
                <span className="text-zinc-500">Overall</span>
                {domainStatus.verified ? (
                  <span className="flex items-center gap-1 text-emerald-400 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Verified
                  </span>
                ) : (
                  <DnsStatusBadge status="not_checked" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PlatformDomainsSettings;
